/**
 * Node dimensions for layout
 */
export const NODE_WIDTH = 200
// Set nodes are slightly more than 3x regular nodes (3 nodes + 2 gaps = 620px, so 630px)
export const SET_NODE_WIDTH = 630
// Top bar height: 22px (accommodates text-[10px] with padding)
export const TOP_BAR_HEIGHT = 22
// Thumbnail height (16:9 aspect ratio) - must match exactly what CSS renders
export const THUMBNAIL_HEIGHT = (NODE_WIDTH * 9) / 16 // 112.5px exactly
// Set node thumbnail height (maintains 16:9 aspect ratio)
export const SET_THUMBNAIL_HEIGHT = (SET_NODE_WIDTH * 9) / 16 // 354.375px
// Total node height: top bar + thumbnail
export const NODE_HEIGHT = TOP_BAR_HEIGHT + THUMBNAIL_HEIGHT // 134.5px
// Total set node height: top bar + thumbnail
export const SET_NODE_HEIGHT = TOP_BAR_HEIGHT + SET_THUMBNAIL_HEIGHT // 376.375px
// Audio node dimensions - "hotdog" style: wide and narrow to distinguish from video nodes
export const AUDIO_NODE_WIDTH = 300
export const AUDIO_CONTENT_HEIGHT = 75
export const AUDIO_NODE_HEIGHT = TOP_BAR_HEIGHT + AUDIO_CONTENT_HEIGHT // 97px

export const NODE_GAP_X = 14 // Horizontal gap between variations (tighter per user feedback)
export const NODE_GAP_Y = 15 // Vertical gap — V2 default (no bottom gutter)
/** V3 vertical gap accounts for the 22px bottom gutter below each node. */
export const NODE_GAP_Y_V3 = 36

/**
 * Scrub movement threshold for re-showing preview after click (normalized 0-1)
 */
export const SCRUB_MOVEMENT_THRESHOLD = 0.02

/**
 * Delay before hiding hover preview (ms)
 */
export const HOVER_TIMEOUT_DELAY = 50

/**
 * Width of the vertical tool rail (left-edge icon dock).
 * Used by FloatingLeftPanel to offset itself when the rail is visible.
 */
export const TOOL_RAIL_WIDTH = 60
export const TOOL_RAIL_INSET = 28

// ============================================================================
// ASPECT RATIO-AWARE SIZING
// ============================================================================

/**
 * Target area for area-preserving node sizing.
 * Maintains consistent visual weight across different aspect ratios.
 */
export const TARGET_THUMBNAIL_AREA = NODE_WIDTH * THUMBNAIL_HEIGHT // ~22,500 px²

/**
 * Default aspect ratio (16:9) used when aspect ratio is unknown
 */
export const DEFAULT_ASPECT_RATIO = 16 / 9

/**
 * Minimum allowed aspect ratio (extreme portrait: 9:21)
 * Prevents nodes from becoming too narrow
 */
export const MIN_ASPECT_RATIO = 27 / 64 // ~0.42

/**
 * Maximum allowed aspect ratio (ultrawide: 21:9)
 * Prevents nodes from becoming too wide
 */
export const MAX_ASPECT_RATIO = 64 / 27 // ~2.37

/**
 * Maximum node height (for 9:21 extreme portrait content) - used for grid spacing
 * to prevent overlap when nodes have different aspect ratios.
 * Based on MIN_ASPECT_RATIO (9:21 ≈ 0.42).
 */
export const MAX_NODE_HEIGHT = TOP_BAR_HEIGHT + Math.sqrt(TARGET_THUMBNAIL_AREA / MIN_ASPECT_RATIO) // ~249px

/**
 * Maximum node width (for 21:9 ultrawide content) - used for collision detection
 * to prevent overlap when nodes have different aspect ratios
 */
export const MAX_NODE_WIDTH = Math.sqrt(TARGET_THUMBNAIL_AREA * MAX_ASPECT_RATIO) // ~229px

/**
 * Clamp aspect ratio to prevent extreme node sizes
 */
export function clampAspectRatio(ratio: number): number {
  // Math.max/min propagate NaN, so without this guard a NaN ratio would pass
  // straight through as NaN node dimensions and OOM the Pixi rounded-rect tracer.
  if (!Number.isFinite(ratio)) return DEFAULT_ASPECT_RATIO
  return Math.max(MIN_ASPECT_RATIO, Math.min(MAX_ASPECT_RATIO, ratio))
}

/**
 * Parse aspect ratio string to a numeric value for canvas sizing: invalid or
 * missing input falls back to DEFAULT_ASPECT_RATIO, and the result is clamped
 * to the canvas node range [MIN_ASPECT_RATIO, MAX_ASPECT_RATIO].
 *
 * Supported formats:
 * - Standard colon format: "16:9", "9:16", "1:1" etc.
 * - Flux-style underscore format: "portrait_16_9", "landscape_4_3", "square_hd", "square"
 */
export function parseAspectRatio(aspectRatioString?: string): number {
  const ratio = parseAspectRatioOrNull(aspectRatioString)
  return ratio == null ? DEFAULT_ASPECT_RATIO : clampAspectRatio(ratio)
}

/**
 * Pure parser: same formats as `parseAspectRatio`, but returns the true ratio
 * unclamped, and null for invalid or missing input. Use for equivalence checks
 * between model aspect-ratio options, where the canvas policies would corrupt
 * the comparison both ways: the 16:9 fallback makes 'auto' pass for a real
 * 16:9, and clamping folds distinct ultrawide shapes (21:9, 3:1, 4:1) into
 * false equality at the canvas max. Clamping is applied by `parseAspectRatio`,
 * which owns canvas sizing.
 *
 * NOTE: The backend has a mirrored version of this logic in
 * backend/src/generation/aspectRatio.ts → parseAspectRatioToNumber()
 * (undefined instead of null). Keep both in sync when modifying the parsing.
 */
export function parseAspectRatioOrNull(aspectRatioString?: string): number | null {
  if (!aspectRatioString) return null
  const trimmed = aspectRatioString.trim()

  if (trimmed === '21:9') return MAX_ASPECT_RATIO
  if (trimmed === '9:21') return MIN_ASPECT_RATIO

  // Standard colon format: "16:9", "9:16", "1:1"
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':')
    if (parts.length !== 2) return null

    const w = parseFloat(parts[0])
    const h = parseFloat(parts[1])

    if (!w || !h || h === 0 || isNaN(w) || isNaN(h)) return null

    return w / h
  }

  // Flux-style formats: "portrait_16_9" → 9/16, "landscape_4_3" → 4/3, "square_hd" → 1
  if (trimmed.startsWith('portrait_')) {
    const nums = trimmed.replace('portrait_', '').split('_')
    if (nums.length === 2) {
      const w = parseFloat(nums[0])
      const h = parseFloat(nums[1])
      if (w && h && !isNaN(w) && !isNaN(h)) {
        // Portrait: flip the ratio (portrait_16_9 means 9:16)
        return h / w
      }
    }
  }

  if (trimmed.startsWith('landscape_')) {
    const nums = trimmed.replace('landscape_', '').split('_')
    if (nums.length === 2) {
      const w = parseFloat(nums[0])
      const h = parseFloat(nums[1])
      if (w && h && !isNaN(w) && !isNaN(h)) {
        return w / h
      }
    }
  }

  if (trimmed.startsWith('square')) {
    return 1
  }

  return null
}

/**
 * Node dimensions calculated from aspect ratio
 */
export interface NodeDimensions {
  /** Total node width (thumbnail area) */
  width: number
  /** Total node height including top bar */
  height: number
  /** Thumbnail width (same as width) */
  thumbnailWidth: number
  /** Thumbnail height (excluding top bar) */
  thumbnailHeight: number
}

/**
 * Calculate node dimensions that preserve visual area while matching aspect ratio.
 * Uses the formula: area = width * height = constant
 * For a given aspect ratio r = w/h: width = sqrt(area * r), height = sqrt(area / r)
 */
export function calculateNodeDimensions(aspectRatio: number): NodeDimensions {
  const clampedRatio = clampAspectRatio(aspectRatio)

  const thumbnailWidth = Math.sqrt(TARGET_THUMBNAIL_AREA * clampedRatio)
  const thumbnailHeight = Math.sqrt(TARGET_THUMBNAIL_AREA / clampedRatio)

  return {
    width: Math.round(thumbnailWidth),
    height: Math.round(thumbnailHeight + TOP_BAR_HEIGHT),
    thumbnailWidth: Math.round(thumbnailWidth),
    thumbnailHeight: Math.round(thumbnailHeight),
  }
}
